import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Store, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import api from "@/lib/api";

export default function Confirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isTerms = params.get("terms") === "1";
  const [state, setState] = useState(isTerms ? "terms" : "checking"); // checking | paid | failed | timeout | terms
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (isTerms) { clear(); return; }
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/shop/checkout/status/${sessionId}`);
        setOrder(data.order);
        if (data.payment_status === "paid") { setState("paid"); clear(); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="bg-cream min-h-screen">
      <Seo title="Confirmation de commande" path="/boutique/confirmation" />
      <div className="max-w-xl mx-auto px-6 pt-40 pb-32 text-center">
        {state === "checking" && (<><Loader2 className="mx-auto animate-spin text-brand mb-4" size={40} data-testid="conf-checking" /><p className="text-stone">Vérification du paiement…</p></>)}
        {state === "paid" && (
          <div data-testid="conf-paid">
            <CheckCircle2 className="mx-auto text-green-600 mb-4" size={56} />
            <h1 className="font-display text-4xl text-forest">Merci pour votre commande !</h1>
            <p className="mt-3 text-stone">Un email de confirmation vient de vous être envoyé.</p>
            {order && (
              <div className="mt-8 text-left rounded-2xl bg-white border border-border p-6">
                <p className="font-semibold text-forest mb-3">Récapitulatif</p>
                {order.items?.map((i, k) => <div key={k} className="flex justify-between text-sm py-1"><span className="text-stone">{i.name} × {i.quantity}</span><span className="text-forest">{(i.price * i.quantity).toFixed(2)} €</span></div>)}
                <div className="flex justify-between text-sm py-1 border-t border-border mt-2 pt-2"><span className="text-stone">Livraison</span><span className="text-forest">{order.delivery_fee ? `${order.delivery_fee.toFixed(2)} €` : "Gratuit"}</span></div>
                <div className="flex justify-between font-semibold pt-2"><span className="text-forest">Total</span><span className="text-forest">{order.total?.toFixed(2)} €</span></div>
                <div className="mt-4 flex items-center gap-2 text-sm text-stone">{order.delivery?.mode === "delivery" ? <><Truck size={16} className="text-brand" /> Livraison — notre service client vous appellera pour un RDV.</> : <><Store size={16} className="text-brand" /> Retrait à Saint-Victoret.</>}</div>
              </div>
            )}
            <Link to="/boutique" className="inline-block mt-8 rounded-full bg-brand text-cream px-6 py-3 font-semibold hover:bg-forest">Continuer mes achats</Link>
          </div>
        )}
        {state === "terms" && (
          <div data-testid="conf-terms">
            <CheckCircle2 className="mx-auto text-green-600 mb-4" size={56} />
            <h1 className="font-display text-4xl text-forest">Commande enregistrée !</h1>
            <p className="mt-3 text-stone">Votre commande est validée en <strong>paiement à 30 jours fin de mois</strong>. Elle sera facturée selon vos conditions professionnelles. Notre service client vous recontacte pour l'organisation.</p>
            <Link to="/compte" className="inline-block mt-8 rounded-full bg-brand text-cream px-6 py-3 font-semibold hover:bg-forest mr-2">Voir mon compte</Link>
            <Link to="/boutique" className="inline-block mt-8 rounded-full border border-brand text-brand px-6 py-3 font-semibold hover:bg-brand/5">Continuer mes achats</Link>
          </div>
        )}
        {(state === "failed" || state === "timeout") && (
          <div data-testid="conf-failed">
            <XCircle className="mx-auto text-red-500 mb-4" size={56} />
            <h1 className="font-display text-3xl text-forest">{state === "timeout" ? "Paiement en cours de vérification" : "Paiement non abouti"}</h1>
            <p className="mt-3 text-stone">{state === "timeout" ? "Si le montant a été débité, vous recevrez un email. Sinon réessayez." : "Votre paiement n'a pas pu être confirmé."}</p>
            <Link to="/boutique/panier" className="inline-block mt-8 rounded-full border border-brand text-brand px-6 py-3 font-semibold hover:bg-brand/5">Retour au panier</Link>
          </div>
        )}
      </div>
    </div>
  );
}
